import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { ResizableTableHead, useResizableColumns } from "@/components/ui/resizable-table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tooltip as STooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Download, Mail, UserPlus, Trash2, Loader2, Search, Phone, Globe, MapPin, Star, Zap, Edit2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

function Tip({ label, children }) {
  return (
    <STooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent className="bg-[#2F4F4F] text-white text-[11px] px-2.5 py-1 rounded-md">{label}</TooltipContent>
    </STooltip>
  );
}

const inputCls = "border-[#BFBFBF] bg-[#FBF9F5] focus:border-[#2F4F4F] focus:ring-[#2F4F4F]/10 rounded-lg text-sm";

export default function LeadsPanel({ onConvert }) {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSector, setFilterSector] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [search, setSearch] = useState("");
  const [editDialog, setEditDialog] = useState(null);
  const [demoDialog, setDemoDialog] = useState(null);

  const cols = useResizableColumns("leads-table", {
    entreprise: 260,
    secteur: 160,
    ville: 140,
    email: 220,
    telephone: 140,
    score: 80,
    statut: 110,
    actions: 170,
  });

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/admin/leads?page=${page}&limit=25`;
      if (filterStatus) url += `&status=${filterStatus}`;
      if (filterSector) url += `&sector=${encodeURIComponent(filterSector)}`;
      if (filterCity) url += `&city=${encodeURIComponent(filterCity)}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const { data } = await api.get(url);
      setLeads(data.leads);
      setTotal(data.total);
      setPages(data.pages);
    } catch (e) {
      toast.error("Erreur chargement leads");
    }
    setLoading(false);
  }, [page, filterStatus, filterSector, filterCity, search]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/leads/stats");
      setStats(data);
    } catch {}
  }, []);

  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const handleImport = async () => {
    setImporting(true);
    try {
      const { data } = await api.post("/admin/leads/import");
      if (data.error) {
        toast.error(`Erreur: ${data.error}`);
      } else {
        toast.success(`${data.imported} lead(s) importé(s), ${data.duplicates} doublon(s) ignoré(s)`);
        loadLeads();
        loadStats();
      }
    } catch (e) {
      toast.error("Erreur lors de l'import");
    }
    setImporting(false);
  };

  const handleSendEmail = async (lead) => {
    if (!lead.email) {
      toast.error("Ce lead n'a pas d'email. Utilisez le téléphone.");
      return;
    }
    try {
      await api.post(`/admin/leads/${lead.id}/send-email`);
      toast.success(`Email envoyé à ${lead.email}`);
      loadLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur envoi email");
    }
  };

  const handleConvert = async (lead) => {
    try {
      await api.post(`/admin/leads/${lead.id}/convert`, { site_demo: "" });
      toast.success(`${lead.name} converti — email sans démo envoyé`);
      loadLeads();
      loadStats();
      if (onConvert) onConvert();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur conversion");
    }
  };

  const handleConvertWithDemo = async (lead, siteDemo) => {
    try {
      await api.post(`/admin/leads/${lead.id}/convert`, { site_demo: siteDemo });
      toast.success(`${lead.name} converti — email avec démo envoyé`);
      setDemoDialog(null);
      loadLeads();
      loadStats();
      if (onConvert) onConvert();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur conversion");
    }
  };

  const handleDelete = async (lead) => {
    try {
      await api.delete(`/admin/leads/${lead.id}`);
      toast.success("Lead supprimé");
      loadLeads();
      loadStats();
    } catch (e) {
      toast.error("Erreur suppression");
    }
  };

  const statusBadge = (status) => {
    switch (status) {
      case "new": return <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px] rounded-full">Nouveau</Badge>;
      case "contacted": return <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] rounded-full">Contacté</Badge>;
      case "converted": return <Badge className="bg-green-100 text-green-700 border-0 text-[10px] rounded-full">Converti</Badge>;
      default: return <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px] rounded-full">{status}</Badge>;
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-semibold text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Prospection Automatique
            </h3>
            <p className="text-xs text-[#6B6B6B] mt-0.5">Leads depuis Prospecting Radar — Import quotidien à 6h</p>
          </div>
          <Button data-testid="import-leads-btn" onClick={handleImport} disabled={importing}
            className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full" size="sm">
            {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            Importer maintenant
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total", value: stats.total || 0, color: "text-[#2F4F4F]" },
            { label: "Nouveaux", value: stats.new || 0, color: "text-blue-600" },
            { label: "Contactés", value: stats.contacted || 0, color: "text-amber-600" },
            { label: "Convertis", value: stats.converted || 0, color: "text-green-600" },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-[#BFBFBF] rounded-lg p-3 text-center">
              <p className={`text-xl font-bold ${s.color}`} style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{s.value}</p>
              <p className="text-[10px] text-[#6B6B6B] uppercase tracking-wider mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[180px] max-w-[280px]">
            <Search className="absolute left-2.5 top-2 w-3.5 h-3.5 text-[#6B6B6B]" />
            <Input data-testid="leads-search" placeholder="Rechercher..." value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-8 h-8 text-xs border-[#BFBFBF] rounded-full" />
          </div>
          <Select value={filterStatus || "all"} onValueChange={(v) => { setFilterStatus(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="h-8 text-xs border-[#BFBFBF] bg-white rounded-full w-[130px]"><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent className="bg-white border-[#BFBFBF]">
              <SelectItem value="all">Tous statuts</SelectItem>
              <SelectItem value="new">Nouveaux</SelectItem>
              <SelectItem value="contacted">Contactés</SelectItem>
              <SelectItem value="converted">Convertis</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterSector || "all"} onValueChange={(v) => { setFilterSector(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="h-8 text-xs border-[#BFBFBF] bg-white rounded-full w-[140px]"><SelectValue placeholder="Secteur" /></SelectTrigger>
            <SelectContent className="bg-white border-[#BFBFBF]">
              <SelectItem value="all">Tous secteurs</SelectItem>
              {(stats.sectors || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterCity || "all"} onValueChange={(v) => { setFilterCity(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="h-8 text-xs border-[#BFBFBF] bg-white rounded-full w-[140px]"><SelectValue placeholder="Ville" /></SelectTrigger>
            <SelectContent className="bg-white border-[#BFBFBF]">
              <SelectItem value="all">Toutes villes</SelectItem>
              {(stats.cities || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#BFBFBF] rounded-xl overflow-hidden">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="bg-[#FBF9F5]/50">
                <ResizableTableHead id="entreprise" width={cols.widths.entreprise} onWidthChange={(w) => cols.setWidth("entreprise", w)} className="text-[#6B6B6B] text-xs">Entreprise</ResizableTableHead>
                <ResizableTableHead id="secteur" width={cols.widths.secteur} onWidthChange={(w) => cols.setWidth("secteur", w)} className="text-[#6B6B6B] text-xs">Secteur</ResizableTableHead>
                <ResizableTableHead id="ville" width={cols.widths.ville} onWidthChange={(w) => cols.setWidth("ville", w)} className="text-[#6B6B6B] text-xs">Ville</ResizableTableHead>
                <ResizableTableHead id="email" width={cols.widths.email} onWidthChange={(w) => cols.setWidth("email", w)} className="text-[#6B6B6B] text-xs">Email</ResizableTableHead>
                <ResizableTableHead id="telephone" width={cols.widths.telephone} onWidthChange={(w) => cols.setWidth("telephone", w)} className="text-[#6B6B6B] text-xs">Téléphone</ResizableTableHead>
                <ResizableTableHead id="score" width={cols.widths.score} onWidthChange={(w) => cols.setWidth("score", w)} className="text-[#6B6B6B] text-xs">Score</ResizableTableHead>
                <ResizableTableHead id="statut" width={cols.widths.statut} onWidthChange={(w) => cols.setWidth("statut", w)} className="text-[#6B6B6B] text-xs">Statut</ResizableTableHead>
                <ResizableTableHead id="actions" width={cols.widths.actions} onWidthChange={(w) => cols.setWidth("actions", w)} className="text-[#6B6B6B] text-xs text-right">Actions</ResizableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[#2F4F4F]" /></TableCell></TableRow>
              ) : leads.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-[#6B6B6B]/50 py-12 text-sm">Aucun lead trouvé</TableCell></TableRow>
              ) : leads.map((lead) => (
                <TableRow key={lead.id} className="hover:bg-[#FBF9F5]/50 transition-colors">
                  <TableCell>
                    <div>
                      <p className="font-medium text-[#2F4F4F] text-sm truncate" title={lead.name}>{lead.name}</p>
                      {lead.rating > 0 && (
                        <span className="text-[10px] text-amber-600 flex items-center gap-0.5 mt-0.5">
                          <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" /> {lead.rating} ({lead.reviews_count})
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-[#6B6B6B] text-xs">{lead.sector}</TableCell>
                  <TableCell className="text-[#6B6B6B] text-xs">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{lead.city}</span>
                  </TableCell>
                  <TableCell className="text-xs">
                    {lead.email ? (
                      <span className="text-[#2F4F4F]">{lead.email}</span>
                    ) : (
                      <span className="text-[#999] italic">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {lead.phone ? (
                      <a href={`tel:${lead.phone.replace(/\s/g, '')}`} className="text-[#2F4F4F] hover:underline flex items-center gap-1">
                        <Phone className="w-3 h-3" />{lead.phone}
                      </a>
                    ) : (
                      <span className="text-[#999] italic">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Zap className="w-3 h-3 text-amber-500" />
                      <span className="text-xs font-semibold text-[#2F4F4F]">{lead.score}</span>
                    </div>
                  </TableCell>
                  <TableCell>{statusBadge(lead.status)}</TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-0.5">
                      {lead.google_maps_url && (
                        <Tip label="Voir la fiche Google Maps">
                          <a href={lead.google_maps_url} target="_blank" rel="noopener noreferrer">
                            <Button data-testid={`lead-maps-${lead.id}`} variant="ghost" size="icon" className="h-7 w-7 text-[#C46A4A] hover:text-[#A6553A] hover:bg-[#C46A4A]/10">
                              <MapPin className="w-3.5 h-3.5" />
                            </Button>
                          </a>
                        </Tip>
                      )}
                      <Tip label="Modifier la fiche">
                        <Button data-testid={`lead-edit-${lead.id}`} variant="ghost" size="icon" className="h-7 w-7 text-[#6B6B6B] hover:text-[#2F4F4F]" onClick={() => setEditDialog(lead)}>
                          <Edit2 className="w-3.5 h-3.5" />
                        </Button>
                      </Tip>
                      {lead.email && lead.status !== "converted" && (
                        <Tip label="Convertir sans démo">
                          <Button data-testid={`lead-convert-${lead.id}`} variant="ghost" size="icon" className="h-7 w-7 text-[#2F4F4F] hover:text-[#1F3535]" onClick={() => handleConvert(lead)}>
                            <UserPlus className="w-3.5 h-3.5" />
                          </Button>
                        </Tip>
                      )}
                      {lead.email && lead.status !== "converted" && (
                        <Tip label="Convertir avec démo">
                          <Button data-testid={`lead-convert-demo-${lead.id}`} variant="ghost" size="icon" className="h-7 w-7 text-[#3F6B6B] hover:text-[#4A6B51]" onClick={() => setDemoDialog(lead)}>
                            <Globe className="w-3.5 h-3.5" />
                          </Button>
                        </Tip>
                      )}
                      <Tip label="Supprimer">
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-[#6B6B6B] hover:text-red-500" onClick={() => handleDelete(lead)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </Tip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex justify-center gap-2 items-center">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="border-[#BFBFBF] text-[#6B6B6B] text-xs rounded-full">Précédent</Button>
            <span className="text-xs text-[#6B6B6B]">{page} / {pages}</span>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}
              className="border-[#BFBFBF] text-[#6B6B6B] text-xs rounded-full">Suivant</Button>
          </div>
        )}

        {/* Demo Convert Dialog */}
        {demoDialog && <ConvertWithDemoDialog lead={demoDialog} onClose={() => setDemoDialog(null)} onConvert={handleConvertWithDemo} />}

        {/* Edit Dialog */}
        {editDialog && <LeadEditDialog lead={editDialog} onClose={() => setEditDialog(null)} onSuccess={() => { setEditDialog(null); loadLeads(); loadStats(); }} />}
      </div>
    </TooltipProvider>
  );
}


function ConvertWithDemoDialog({ lead, onClose, onConvert }) {
  const [siteDemo, setSiteDemo] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!siteDemo.trim()) {
      toast.error("Veuillez renseigner l'URL du site de démonstration");
      return;
    }
    setSaving(true);
    await onConvert(lead, siteDemo.trim());
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md border-[#BFBFBF] bg-white">
        <DialogHeader>
          <DialogTitle className="text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Convertir avec démo</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-[#FBF9F5] rounded-lg p-3 border border-[#BFBFBF]">
            <p className="text-sm font-medium text-[#2F4F4F]">{lead.name}</p>
            <p className="text-xs text-[#6B6B6B]">{lead.email}</p>
          </div>
          <div>
            <Label className="text-xs text-[#6B6B6B]">URL du site de démonstration</Label>
            <Input
              data-testid="convert-demo-url"
              value={siteDemo}
              onChange={(e) => setSiteDemo(e.target.value)}
              className={inputCls}
              placeholder="https://demo.monbeausite.com/nom-entreprise"
              autoFocus
            />
            <p className="text-[10px] text-[#999] mt-1">Le prospect recevra l'email de prospection avec le lien vers son site de démonstration.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full border-[#BFBFBF] text-[#6B6B6B]">Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Convertir et envoyer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function LeadEditDialog({ lead, onClose, onSuccess }) {
  const [form, setForm] = useState({
    name: lead.name || "",
    email: lead.email || "",
    phone: lead.phone || "",
    city: lead.city || "",
    sector: lead.sector || "",
    website: lead.website || "",
    address: lead.address || "",
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/leads/${lead.id}`, form);
      toast.success("Fiche mise à jour");
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg border-[#BFBFBF] bg-white">
        <DialogHeader>
          <DialogTitle className="text-[#2F4F4F]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Modifier le lead</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSave} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#6B6B6B]">Nom / Entreprise</Label>
              <Input data-testid="edit-lead-name" value={form.name} onChange={(e) => update("name", e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Email</Label>
              <Input data-testid="edit-lead-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className={inputCls} placeholder="contact@entreprise.com" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#6B6B6B]">Téléphone</Label>
              <Input data-testid="edit-lead-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Secteur</Label>
              <Input data-testid="edit-lead-sector" value={form.sector} onChange={(e) => update("sector", e.target.value)} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#6B6B6B]">Ville</Label>
              <Input data-testid="edit-lead-city" value={form.city} onChange={(e) => update("city", e.target.value)} className={inputCls} />
            </div>
            <div>
              <Label className="text-xs text-[#6B6B6B]">Site web</Label>
              <Input data-testid="edit-lead-website" value={form.website} onChange={(e) => update("website", e.target.value)} className={inputCls} placeholder="https://..." />
            </div>
          </div>
          <div>
            <Label className="text-xs text-[#6B6B6B]">Adresse</Label>
            <Input data-testid="edit-lead-address" value={form.address} onChange={(e) => update("address", e.target.value)} className={inputCls} />
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full border-[#BFBFBF] text-[#6B6B6B]">Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-[#2F4F4F] text-white hover:bg-[#1F3535] rounded-full">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
